import axios from "axios";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
    timeout: 5000,
});

// Function to refresh the access token
export const refreshAccessToken = async () => {
    const authTokens = JSON.parse(localStorage.getItem("AuthTokens"));

    if (!authTokens || !authTokens.refresh) {
        console.error("No refresh token available. Logging out...");
        localStorage.removeItem("AuthTokens");
        window.location.href = "/sign-in";
        throw new Error("No refresh token available");
    }

    try {
        const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/token/refresh/`,
            { refresh: authTokens.refresh },
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );

        const newAuthTokens = {
            access: response.data.access,
            refresh: response.data.refresh || authTokens.refresh,
        };

        // Update tokens in local storage
        localStorage.setItem("AuthTokens", JSON.stringify(newAuthTokens));

        // Update axios instance headers with the new token
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAuthTokens.access}`;

        console.log("Access token refreshed successfully");
        return newAuthTokens;
    } catch (error) {
        console.error("Error refreshing access token:", error);

        // Handle token refresh failure (e.g., log out the user)
        localStorage.removeItem("AuthTokens");
        window.location.href = "/sign-in";
        throw error;
    }
};

// Attach access token dynamically to requests
axiosInstance.interceptors.request.use(
    (config) => {
        const authTokens = JSON.parse(localStorage.getItem("AuthTokens"));

        // Skip adding Authorization header if skipAuth flag is passed
        if (!config.skipAuth) {
            if (authTokens && authTokens.access) {
                config.headers.Authorization = `Bearer ${authTokens.access}`;
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Handle response errors (e.g., token expiration)
axiosInstance.interceptors.response.use(
    (response) => response, // Pass successful responses through
    async (error) => {
        const originalRequest = error.config;

        // For skipAuth requests, do not retry or refresh tokens
        if (originalRequest.skipAuth) {
            console.warn("Skipping token refresh for skipAuth request.");
            return Promise.reject(error);
        }

        // Handle token refresh for authenticated requests
        const authTokens = JSON.parse(localStorage.getItem("AuthTokens"));
        if (!authTokens) {
            console.error("No auth tokens available. Logging out...");
            localStorage.removeItem("AuthTokens");
            window.location.href = "/sign-in";
            return Promise.reject(error);
        }

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newTokens = await refreshAccessToken();

                // Retry the original request with the refreshed token
                if (newTokens && newTokens.access) {
                    originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
                    return axiosInstance(originalRequest);
                }
            } catch (refreshError) {
                console.error("Token refresh failed during request retry:", refreshError);
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
